import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ApiError, resendConfirmation } from "../api.js";
import { isStaff, useAuth } from "../auth.js";
import { Brand } from "../Brand.js";
import { postSignInDestination } from "../signInDestination.js";

export function SignIn() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const stateEmail = location.state && typeof location.state === "object" && "email" in location.state && typeof location.state.email === "string"
    ? location.state.email
    : "";
  const [email, setEmail] = useState(stateEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Set when the password was right but the address was never confirmed.
   *
   * Worth its own state rather than another line of red text: the useful thing
   * to offer is another link, and somebody in this position has usually lost
   * the first one.
   */
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [resent, setResent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setUnconfirmed(false);
    setResent(false);
    try {
      const account = await signIn(email, password);
      // A signed-out screen can be shown at any private/stale URL because auth
      // is checked before the app routes are rendered. Keeping that URL after
      // login can therefore wake the staff shell on a route that no longer
      // exists or is not available to this account; the sidebar then appears
      // but the main area is a dead end until somebody clicks Today manually.
      //
      // So the default remains the app root, and the authenticated shell
      // chooses the correct home: Today for normal tutors, Diary for staff
      // without the Today workspace, and Home for family/student accounts. The
      // exceptions are addresses the Hub itself sends people to from outside:
      // lesson resource links, lesson notes and the coupon email's Book a lesson
      // link. See `signInDestination.ts` for why that list is an allowlist.
      navigate(postSignInDestination(pathname, { staff: isStaff(account) }), {
        replace: true,
      });
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not sign in.");
      if (cause instanceof ApiError && cause.code === "unconfirmed") setUnconfirmed(true);
      setBusy(false);
    }
  }

  async function resend() {
    setResent(true);
    // Nothing is reported back. The endpoint answers the same way whatever it
    // finds, and a page that said otherwise would undo that.
    await resendConfirmation(email).catch(() => undefined);
  }

  return (
    <div className="signin">
      <form onSubmit={submit}>
        <Brand title="Wirral Music Factory" subtitle="Hub" className="brand-auth" />

        {error && <div className="error small">{error}</div>}

        {unconfirmed &&
          (resent ? (
            <div className="notice small">
              If that address is waiting to be confirmed, another link is on its way.
            </div>
          ) : (
            <button type="button" className="quiet small" onClick={() => void resend()}>
              Send the confirmation link again
            </button>
          ))}

        <label className="small muted" htmlFor="identifier">
          Email or username
        </label>
        {/* Deliberately type="text". A child signing in as "matilda.charles"
            would fail a browser's email validation and be told, unhelpfully,
            to include an @. */}
        <input
          id="identifier"
          type="text"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <label className="small muted" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <button type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <Link className="small muted" to="/forgot-password">
          Forgotten your password?
        </Link>

        <Link className="small muted" to="/help">Help & guides</Link>

        <Link className="small muted" to="/register">
          New here? Create an account
        </Link>
      </form>
    </div>
  );
}
